import { useCallback, useEffect, useState } from 'react';

/**
 * V2 Voice GM (2026-06-02) — per-campaign voice log persistence.
 *
 * Stores STT transcripts as a chronological list under
 * `briefing-room:voice-log:{campaignId}` so the Voice Log drawer can
 * show what the player has said this session. V3+ will also push GM
 * replies + tool-call results into this same log so the drawer becomes
 * a full conversation history.
 *
 * Cap: 200 entries per campaign (older entries dropped). Each entry is
 * tagged with a monotonic timestamp string (no Date.now per the
 * non-determinism rule — we read performance.now() inside the writer).
 */

export type VoiceLogRole = 'player' | 'gm' | 'system';

export interface VoiceLogEntry {
  id: string;
  ts: number;
  role: VoiceLogRole;
  text: string;
}

const MAX_ENTRIES = 200;

function storageKey(campaignId: string): string {
  return `briefing-room:voice-log:${campaignId}`;
}

function readStored(campaignId: string): VoiceLogEntry[] {
  if (typeof window === 'undefined' || campaignId.length === 0) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(storageKey(campaignId));
    if (raw === null) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (e): e is VoiceLogEntry =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as VoiceLogEntry).id === 'string' &&
        typeof (e as VoiceLogEntry).ts === 'number' &&
        typeof (e as VoiceLogEntry).text === 'string',
    );
  } catch {
    return [];
  }
}

function writeStored(campaignId: string, entries: VoiceLogEntry[]): void {
  if (typeof window === 'undefined' || campaignId.length === 0) {
    return;
  }
  try {
    window.localStorage.setItem(storageKey(campaignId), JSON.stringify(entries));
  } catch {
    // localStorage quota / privacy mode — degrade quietly.
  }
}

let monotonicCounter = 0;
function nextId(): string {
  monotonicCounter += 1;
  return `vl-${monotonicCounter.toString(36)}`;
}

export function useVoiceLog(campaignId: string): {
  entries: VoiceLogEntry[];
  append(role: VoiceLogRole, text: string): void;
  clear(): void;
} {
  const [entries, setEntries] = useState<VoiceLogEntry[]>(() => readStored(campaignId));

  // Re-load when campaign changes.
  useEffect(() => {
    setEntries(readStored(campaignId));
  }, [campaignId]);

  // Cross-tab + same-tab sync via storage event.
  useEffect(() => {
    const handler = (e: Event) => {
      if (e instanceof CustomEvent && typeof e.detail === 'string' && e.detail === campaignId) {
        setEntries(readStored(campaignId));
      } else if (e.type === 'storage') {
        setEntries(readStored(campaignId));
      }
    };
    window.addEventListener('briefing-room:voice-log-changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('briefing-room:voice-log-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, [campaignId]);

  const append = useCallback(
    (role: VoiceLogRole, text: string) => {
      if (text.length === 0 || campaignId.length === 0) {
        return;
      }
      setEntries((prev) => {
        // Reading performance.now() inside the setter keeps the timestamp
        // close to the actual append; we do NOT use Date.now (non-deterministic
        // per workflow / wakeup constraints) and we cannot rely on react state
        // for time. performance.now is monotonic from page load.
        const ts =
          typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : 0;
        const next = [...prev, { id: nextId(), ts, role, text }];
        const trimmed = next.length > MAX_ENTRIES ? next.slice(next.length - MAX_ENTRIES) : next;
        writeStored(campaignId, trimmed);
        window.dispatchEvent(
          new CustomEvent('briefing-room:voice-log-changed', { detail: campaignId }),
        );
        return trimmed;
      });
    },
    [campaignId],
  );

  const clear = useCallback(() => {
    if (campaignId.length === 0) {
      return;
    }
    setEntries([]);
    writeStored(campaignId, []);
    window.dispatchEvent(
      new CustomEvent('briefing-room:voice-log-changed', { detail: campaignId }),
    );
  }, [campaignId]);

  return { entries, append, clear };
}
